// Copyright (c) Bentley Systems
import { SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import {
  TablePaginator,
  TablePaginatorRendererProps,
} from "@itwin/itwinui-react";

type SortDescriptor = { id: string; desc: boolean };

const DEFAULT_PAGE_SIZE_LIST = [10, 25, 50, 100] as const;

/**
 * Hook that encapsulates all server-side pagination, sorting and filtering state
 * used by OData-backed tables.
 *
 * Returns ready-to-spread props for `<Table>` and `<TablePaginator>`.
 */
export const useODataTableState = (options?: {
  defaultPageSize?: number;
  defaultSortBy?: SortDescriptor[];
  pageSizeList?: number[];
}) => {
  const {
    defaultPageSize = 25,
    defaultSortBy = [{ id: "name", desc: false }],
    pageSizeList: customPageSizeList,
  } = options ?? {};

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy, setSortBy] = useState<SortDescriptor[]>(defaultSortBy);
  const [filters, setFilters] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const pageSizeList = useMemo(
    () => customPageSizeList ?? [...DEFAULT_PAGE_SIZE_LIST],
    [customPageSizeList],
  );

  // Keep table filters in sync with state (so clearing works)
  const [tableFiltersState, setTableFiltersState] = useState<any[]>([]);
  useEffect(() => {
    setTableFiltersState(filters);
  }, [filters]);

  // iTwinUI's Table calls onSort/onFilter synchronously during its render
  // phase (when reconciling initialState). Deferring state updates via
  // queueMicrotask avoids the React warning: "Cannot update a component
  // while rendering a different component".
  const handleSort = useCallback((state: any) => {
    queueMicrotask(() => {
      setSortBy(state.sortBy);
      setPageIndex(0);
    });
  }, []);

  const handleFilter = useCallback((fil: SetStateAction<any[]>) => {
    queueMicrotask(() => {
      setFilters(fil);
      setPageIndex(0);
    });
  }, []);

  const paginator = useCallback(
    (props: TablePaginatorRendererProps) => (
      <TablePaginator
        {...props}
        pageSizeList={pageSizeList}
        isLoading={isLoading}
        currentPage={pageIndex}
        pageSize={pageSize}
        totalRowsCount={totalCount}
        onPageChange={setPageIndex}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPageIndex(0);
        }}
      />
    ),
    [pageIndex, pageSize, totalCount, pageSizeList, isLoading],
  );

  /** Build OData `$orderby` query param from current sort state */
  const buildOrderBy = useCallback(
    (columnToODataPath?: Record<string, string>) => {
      if (!sortBy || sortBy.length === 0) { return ""; }
      const s = sortBy[0];
      const field = columnToODataPath?.[s.id] ?? s.id;
      return `&$orderby=${field} ${s.desc ? "desc" : "asc"}`;
    },
    [sortBy],
  );

  /**
   * Build OData `$filter` query param from current column filter state.
   * Text filters become `contains(...)`. A column listed in `customClauses` is filtered some
   * other way: while it has a filter, its URL-ready clause is used instead, and "" leaves it out.
   */
  const buildFilterStr = useCallback(
    (
      columnToODataPath?: Record<string, string>,
      customClauses?: Record<string, string>,
    ) => {
      let filterStr = "";
      filters.forEach((f) => {
        if (customClauses && Object.prototype.hasOwnProperty.call(customClauses, f.id)) {
          const clause = customClauses[f.id];
          if (f.value && clause) {
            filterStr += ` and ${clause}`;
          }
          return;
        }
        if (f.value && f.value.length >= 3) {
          const field = columnToODataPath?.[f.id] ?? f.id;
          const escaped = f.value.replace(/'/g, "''");
          filterStr += ` and contains(${field},'${encodeURIComponent(escaped)}')`;
        }
      });
      if (filterStr.startsWith(" and ")) {
        filterStr = filterStr.slice(5);
      }
      return filterStr ? `&$filter=${filterStr}` : "";
    },
    [filters],
  );

  return {
    // State
    pageIndex,
    pageSize,
    totalCount,
    sortBy,
    filters,
    isLoading,
    tableFiltersState,

    // Setters
    setPageIndex,
    setPageSize,
    setTotalCount,
    setIsLoading,
    setFilters,

    // Handlers
    handleSort,
    handleFilter,
    paginator,

    // OData helpers
    buildOrderBy,
    buildFilterStr,

    /** Pagination skip value for OData `$skip` */
    skip: pageIndex * pageSize,
    /** Pagination top value for OData `$top` */
    top: pageSize,
  };
};
